import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import { resetQuestionnaire } from '../../actions';
import lodashSet from 'lodash.set';

export interface CompensationStateValues {
  doYouHaveEmployees: Nullable<TYesOrNo>;
  didYouOfferMedicalInsuranceToAnyEmployeesDuringThisYear: Nullable<TYesOrNo>;
  didYouPayAllWagessalariesAndWithholdAllRequiredTaxes: Nullable<TYesOrNo>;
  doesYourBusinessOfferARetirementPlanForEmployees: Nullable<TYesOrNo>;
  doesYourBusinessOfferAnHsaAccountForEmployees: Nullable<TYesOrNo>;
  doesYourBusinessOfferACafeteria124FlexibleSpendingAccountForEmployees: Nullable<TYesOrNo>;
  doesYourBusinessHaveMoreThan50Employees: Nullable<TYesOrNo>;
  didYouPayAnyEmployeesQualifiedFamilyLeaveWages: Nullable<TYesOrNo>;
  didYouPayAnyEmployeesQualifiedSickLeaveWages: Nullable<TYesOrNo>;
  pleaseUploadAllPayrollSummaryReportsPayrollDetailReportsW2SAndW3S: Nullable<string>;
  didYouPayAnyoneWhoIsNotACorporationATotalOf600OrMoreDuringTheYearOrIfYouPaidAnyAmountToAnyAttorneysYouAreRequiredToIssueEachPayeeA1099MiscForm: Nullable<TYesOrNo>;
  didYouMakeAnyPaymentsForWhichYouShouldIssueA1099Misc: Nullable<TYesOrNo>;
  didYouIssueTheRequired1099MiscForms: Nullable<TYesOrNo>;
  howManyForms1099AreYouRequiredToFileForPaymentsYouMadeThisYear: Nullable<TYesOrNo>;
  didYouPayYourselfOrAnyWorkersAsEmployeesThisYear: Nullable<TYesOrNo>;
  didYouHaveAProfessionalAtAccountantsOnAirProcessYourPayrollThisYear: Nullable<TYesOrNo>;
  congratulationsThatMeansMuchLessWorkForYouOnThisTaxInformationweWillImportYourPayrollInformationDirectlyFromOurPayrollSoftware: Nullable<string>;
  didYouKnowWeOfferPayrollServicesifInterestedPleaseAskYourTaxAdvisorForMoreDetails: Nullable<TYesOrNo>;
  howManyFormsW2W3DidYouReceiveThisYear: Nullable<string>;
  didYouPurchaseAnyBusinessAssetsThisYear: Nullable<TYesOrNo>;
  describeAssetsYouPurchased: describeAssetsYouPurchasedRecord;
}

export interface CompensationState {
  values: CompensationStateValues;
}

type describeAssetsYouPurchasedRecord = describeAssetsYouPurchasedRecordRow[];
export type describeAssetsYouPurchasedRecordRow = {
  description: Nullable<string>;
  datePlacedInService: Nullable<string>;
  purchasedPrice: Nullable<string>;
  didYouMakeImprovementsOrRepairsToAnyPropertyEquipmentOrOtherAssets: Nullable<TYesOrNo>;
  pleaseProvideDetailsAndCosts: Nullable<string>;
};
const initialState: CompensationState = {
  values: {
    doYouHaveEmployees: null,
    didYouOfferMedicalInsuranceToAnyEmployeesDuringThisYear: null,
    didYouPayAllWagessalariesAndWithholdAllRequiredTaxes: null,
    doesYourBusinessOfferARetirementPlanForEmployees: null,
    doesYourBusinessOfferAnHsaAccountForEmployees: null,
    doesYourBusinessOfferACafeteria124FlexibleSpendingAccountForEmployees: null,
    doesYourBusinessHaveMoreThan50Employees: null,
    didYouPayAnyEmployeesQualifiedFamilyLeaveWages: null,
    didYouPayAnyEmployeesQualifiedSickLeaveWages: null,
    pleaseUploadAllPayrollSummaryReportsPayrollDetailReportsW2SAndW3S: null,
    didYouPayAnyoneWhoIsNotACorporationATotalOf600OrMoreDuringTheYearOrIfYouPaidAnyAmountToAnyAttorneysYouAreRequiredToIssueEachPayeeA1099MiscForm:
      null,
    didYouMakeAnyPaymentsForWhichYouShouldIssueA1099Misc: null,
    didYouIssueTheRequired1099MiscForms: null,
    howManyForms1099AreYouRequiredToFileForPaymentsYouMadeThisYear: null,
    didYouPayYourselfOrAnyWorkersAsEmployeesThisYear: null,
    didYouHaveAProfessionalAtAccountantsOnAirProcessYourPayrollThisYear: null,
    congratulationsThatMeansMuchLessWorkForYouOnThisTaxInformationweWillImportYourPayrollInformationDirectlyFromOurPayrollSoftware:
      null,
    didYouKnowWeOfferPayrollServicesifInterestedPleaseAskYourTaxAdvisorForMoreDetails:
      null,
    howManyFormsW2W3DidYouReceiveThisYear: null,
    didYouPurchaseAnyBusinessAssetsThisYear: null,
    describeAssetsYouPurchased: [],
  },
};

const CompensationSlice = createSlice({
  name: 'Compensation',
  initialState,
  reducers: {
    reset(state) {
      state = initialState;
    },
    update(state, action: PayloadAction<CompensationStateValues>) {
      state.values = action.payload;
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof CompensationStateValues;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },

    // Update KEY_HERE_LIST if there is a Record Here
    updateFieldArrayKey(
      state: any,
      action: PayloadAction<{
        key: keyof CompensationStateValues | '';
        value: any;
      }>,
    ) {
      const fieldArray =
        action.payload.value &&
        action.payload.value.map(({ ...field }) => ({
          ...field,
        }));
      if (action.payload.key === 'describeAssetsYouPurchased') {
        state.values[action.payload.key] = fieldArray;
      } else {
        lodashSet(state, `values.${action.payload.key}`, fieldArray);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetQuestionnaire, () => initialState);

    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire?.additionalQuestions
              ?.compensation?.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  CompensationSlice.actions;
export default CompensationSlice.reducer;

export const selectCompensation = (state: RootState) =>
  state.taxQuestionnaire.additionalQuestions.compensation;
